import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BatchService, BatchItem } from '../../shared/services/batch.service';
import { CourseService } from '../../shared/core/state/course/course.service';
import { SubscriptionService } from '../../shared/core/state/subscription/subscription.service';
import { FormatterService } from '../../shared/util/formatter.service';

@Component({
  selector: 'black-my-batches',
  templateUrl: './my-batches.component.html',
  styles: [],
  standalone: false,
})
export class MyBatchesComponent implements OnInit, OnDestroy {
  private batchService = inject(BatchService);
  private courseService = inject(CourseService);
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private formatter = inject(FormatterService);

  private destroy$ = new Subject<void>();

  batches: BatchItem[] = [];
  enrolledCourseIds = new Set<string>();
  subscribedClassIds = new Set<string>();
  loading = true;

  // Selected batch for detailed view
  selectedBatch: BatchItem | null = null;

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.loading = true;

    forkJoin({
      batches: this.batchService
        .getBatches(1, 100)
        .pipe(catchError(() => of([]))),
      enrollments: this.courseService
        .getEnrollments()
        .pipe(catchError(() => of([]))),
      subscriptions: this.subscriptionService
        .fetchAllSubscribtions()
        .pipe(catchError(() => of([]))),
    })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ batches, enrollments, subscriptions }: any) => {
          this.batches = Array.isArray(batches)
            ? batches
            : (batches?.docs ?? []);

          // Map active course enrollments
          const courses = enrollments || [];
          this.enrolledCourseIds = new Set(
            courses.map((c: any) => c._id || c.id),
          );

          // Map active subscriptions
          const subs = Array.isArray(subscriptions)
            ? subscriptions
            : subscriptions?.docs || [];
          this.subscribedClassIds = new Set(
            subs
              .filter(
                (s: any) => s.status === 'ACTIVE' || s.status === 'COMPLETE',
              )
              .map(
                (s: any) => s.liveClass?._id ?? s.liveClass?.id ?? s.liveClass,
              )
              .filter(Boolean),
          );

          if (this.batches.length > 0 && !this.selectedBatch) {
            this.selectedBatch = this.batches[0];
          }
        },
      });
  }

  selectBatch(batch: BatchItem): void {
    this.selectedBatch = batch;
  }

  isCourseEnrolled(courseId: string): boolean {
    return this.enrolledCourseIds.has(courseId);
  }

  isClassSubscribed(classId: string): boolean {
    return this.subscribedClassIds.has(classId);
  }

  goToCourse(course: any): void {
    const id = course._id || course.id;
    if (this.isCourseEnrolled(id)) {
      this.router.navigate(['/courses', id, 'playlist']);
    } else {
      this.router.navigate(['/courses', id]);
    }
  }

  goToLiveClass(lc: any): void {
    const id = lc._id || lc.id;
    if (this.isClassSubscribed(id)) {
      this.router.navigate(['/dashboard/learning/live-class', id]);
    } else {
      this.router.navigate(['/live-classes', lc.slug || id]);
    }
  }

  formatDate(date?: string | Date): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getBannerImage(item: any): string {
    return this.formatter.getResourceImagePath(
      item?.settings?.bannerImageResource,
    );
  }

  getInitials(title: string): string {
    return (title || '')
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  }
}
